import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Lock, AlertCircle, ArrowRight, Eye, EyeOff } from "lucide-react";

const CLIENT_SESSION_KEY = "ie_client_authed";
const CLIENT_NAME_KEY = "ie_client_name";
const CLIENT_ID_KEY = "ie_client_id";

export function isClientAuthed(): boolean {
  try {
    return localStorage.getItem(CLIENT_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function getClientName(): string {
  try {
    return localStorage.getItem(CLIENT_NAME_KEY) || "";
  } catch {
    return "";
  }
}

export function getClientId(): string {
  try {
    return localStorage.getItem(CLIENT_ID_KEY) || "";
  } catch {
    return "";
  }
}

export function clearClientAuth() {
  try {
    localStorage.removeItem(CLIENT_SESSION_KEY);
    localStorage.removeItem(CLIENT_NAME_KEY);
    localStorage.removeItem(CLIENT_ID_KEY);
  } catch {
    /* noop */
  }
}

export function saveClientAuth(clientId: string, name: string) {
  try {
    localStorage.setItem(CLIENT_SESSION_KEY, "1");
    localStorage.setItem(CLIENT_NAME_KEY, name);
    localStorage.setItem(CLIENT_ID_KEY, clientId);
  } catch {
    /* noop */
  }
}

export default function ClientLoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resumeState, setResumeState] = useState<"idle" | "checking" | "show" | "none">("idle");
  const resumeName = getClientName();

  useEffect(() => {
    document.title = "Immense Estate — Client Portal";
    if (!isClientAuthed()) { setResumeState("none"); return; }

    // Verify the stored session is still active server-side
    setResumeState("checking");
    fetch("/api/client-verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clientId: getClientId() }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok) {
          setResumeState("show");
        } else {
          clearClientAuth();
          setResumeState("none");
        }
      })
      .catch(() => {
        // Can't reach server — show resume anyway, guard will re-verify
        setResumeState("show");
      });
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/client-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Login failed");
        setLoading(false);
        return;
      }
      saveClientAuth(data.clientId || "", data.name || "");
      navigate("/home");
    } catch {
      setError("Network error — please try again");
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-navy-900 text-white flex flex-col">
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-sky-500/10 blur-[120px]" />
        <div className="absolute -right-56 top-24 h-[680px] w-[680px] rounded-full bg-amber-500/10 blur-[120px]" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[400px] w-[800px] rounded-full bg-emerald-500/5 blur-[140px]" />
      </div>

      {/* Resume session modal */}
      {(resumeState === "checking" || resumeState === "show") && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          {resumeState === "checking" ? (
            <div className="flex flex-col items-center gap-3 text-white/60">
              <div className="h-8 w-8 rounded-full border-2 border-amber-400/40 border-t-amber-400 animate-spin" />
              <span className="text-sm">Verifying session…</span>
            </div>
          ) : (
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="w-full max-w-sm rounded-2xl border border-white/[0.08] bg-[#0d1523] p-8 shadow-2xl text-center"
            >
              <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/30">
                <Building2 className="h-6 w-6 text-navy-950" />
              </div>
              <div className="text-[10px] font-bold tracking-[0.25em] text-amber-400 uppercase mb-1">Already signed in</div>
              <h2 className="text-xl font-bold text-white">
                Welcome back,<br />
                <span className="text-amber-300">{resumeName}</span>
              </h2>
              <p className="mt-2 text-sm text-white/45">You have an active session. Would you like to continue or sign out?</p>
              <div className="mt-6 flex flex-col gap-2">
                <button
                  onClick={() => navigate("/home")}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-3 text-sm font-bold text-navy-950 shadow-lg shadow-amber-500/20 hover:brightness-110 transition"
                >
                  Continue as {resumeName} <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => { clearClientAuth(); setResumeState("none"); }}
                  className="w-full rounded-lg border border-white/10 bg-white/5 py-3 text-sm text-white/60 hover:bg-white/10 transition"
                >
                  Sign out &amp; use different account
                </button>
              </div>
            </motion.div>
          )}
        </div>
      )}

      <header className="relative z-10 flex items-center px-6 py-5 md:px-10">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
            <Building2 className="h-5 w-5 text-amber-400" />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] font-semibold tracking-[0.3em] text-zinc-500 uppercase">Client Portal</div>
            <div className="text-sm font-semibold text-white">Immense Estate</div>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex flex-1 items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Hero text */}
          <div className="mb-8 text-center">
            <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/30">
              <Lock className="h-7 w-7 text-navy-950" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Welcome Back</h1>
            <p className="mt-2 text-sm text-white/50">Sign in with your client credentials to access the estate portal.</p>
          </div>

          <form
            onSubmit={handleLogin}
            className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 shadow-2xl backdrop-blur-xl space-y-4"
          >
            <label className="block">
              <span className="block text-[11px] font-semibold tracking-wider text-white/60 uppercase mb-1.5">
                Username
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                placeholder="your-username"
                className="w-full rounded-lg border border-white/10 bg-navy-950/70 px-3.5 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40"
              />
            </label>

            <label className="block">
              <span className="block text-[11px] font-semibold tracking-wider text-white/60 uppercase mb-1.5">
                Password
              </span>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full rounded-lg border border-white/10 bg-navy-950/70 px-3.5 py-2.5 pr-10 text-sm text-white placeholder-white/30 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>

            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-3 text-sm font-bold tracking-wide text-navy-950 shadow-lg shadow-amber-500/20 transition hover:brightness-110 disabled:opacity-60"
            >
              {loading ? "Signing in…" : (<>Sign In <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </form>

          <p className="mt-6 text-center text-[11px] text-white/30">
            Your credentials were provided by the estate administrator.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
